import React, { useEffect, useState, useMemo } from 'react';
import PageShell, { SectionTitle } from './PageShell';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { CheckCircle2, Circle, Clock, Loader2, Star, Lock, Users, ArrowRight } from 'lucide-react';
import { StudentLinkTeacherWidget } from './TeacherPages';
import { Link, Navigate } from 'react-router-dom';

export const COMPETENCES = [
  { code: 'C1', title: 'Maîtriser', subtitle: 'Le maniement du véhicule', subs: [
    'Connaître et maîtriser son véhicule', 'Entrer, s’installer et sortir du véhicule', "Réaliser les vérifications intérieures et extérieures",
    'Démarrer et s’arrêter', 'Doser l’accélérateur, l’embrayage, le frein',
    'Tenir et tourner le volant', 'Diriger la voiture en avant en ligne droite et en courbe',
    'Reculer en ligne droite et en courbe', 'Regarder autour de soi', 'Adapter l’allure',
    'Anticiper les actions', 'Communiquer avec les autres usagers',
  ]},
  { code: 'C2', title: 'Appréhender', subtitle: 'La route et la circulation', subs: [
    'Rechercher la signalisation', 'Connaître les règles de circulation',
    'Positionner le véhicule sur la chaussée', 'Adapter l’allure aux situations',
    'Détecter, identifier les indices utiles', 'Tourner à droite et à gauche en agglomération',
    'Franchir les intersections et changer de direction', 'Croiser, dépasser, être dépassé',
  ]},
  { code: 'C3', title: 'Circuler', subtitle: 'En conditions difficiles', subs: [
    'Conduire dans des conditions atmosphériques difficiles', 'Conduire de nuit',
    'Conduire sur autoroute / voie rapide', 'Conduire sur route', 'Conduire en agglomération',
    'Conduire en zone urbaine dense', 'Conduire en zone montagneuse', 'Conduire en zone rurale',
    'Conduire avec des passagers', 'Conduire avec un chargement', 'Conduire un véhicule attelé',
  ]},
  { code: 'C4', title: 'Pratiquer', subtitle: 'Une conduite autonome', subs: [
    'Suivre un itinéraire de manière autonome', 'Préparer et réaliser un voyage longue distance',
    'Éco-conduite et conduite citoyenne', 'Faire face à des situations particulières',
    'Réagir face à un accident', 'Savoir donner les premiers secours', 'Garer son véhicule de manière autonome',
  ]},
];

function LivretValidationBanner() {
  const [v, setV] = useState(null);
  useEffect(() => { api.myLivretValidation().then((d) => setV(d.validation)).catch(() => {}); }, []);
  if (!v) return null;
  return (
    <div className="rounded-2xl border border-emerald-700/30 bg-emerald-950/10 p-4 mb-6 flex items-center gap-3 flex-wrap" data-testid="livret-validation-banner">
      <div className="w-9 h-9 rounded-lg bg-emerald-600/20 border border-emerald-600/30 flex items-center justify-center text-emerald-400 shrink-0"><CheckCircle2 size={16} /></div>
      <div className="text-[13px] text-white/80">
        <b className="text-emerald-300">Livret validé par {v.teacher_name}</b> le {new Date(v.validated_at).toLocaleDateString('fr-FR')} — {v.count_acquis} compétence{v.count_acquis > 1 ? 's' : ''} validée{v.count_acquis > 1 ? 's' : ''} par ton enseignant.
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    done: { label: 'Acquis', cls: 'bg-emerald-600/15 border-emerald-600/30 text-emerald-300', icon: CheckCircle2 },
    inprogress: { label: 'En cours', cls: 'bg-yellow-600/15 border-yellow-600/30 text-yellow-300', icon: Clock },
    todo: { label: 'À voir', cls: 'bg-white/5 border-white/10 text-white/50', icon: Circle },
  };
  const { label, cls, icon: Icon } = map[status] || map.todo;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10.5px] font-semibold uppercase tracking-wider ${cls}`}>
      <Icon size={11} /> {label}
    </span>
  );
}

export default function LivretPage() {
  const { user } = useAuth();
  // Le livret est destiné aux élèves uniquement. Les enseignants ECSR consultent
  // les livrets de leurs élèves depuis leur tableau de bord.
  if (user && user.role === 'enseignant') {
    return (
      <PageShell>
        <section className="max-w-3xl mx-auto px-4 lg:px-6 mt-12 sm:mt-20 text-center" data-testid="livret-teacher-notice">
          <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-red-600/15 border border-red-700/30 text-red-400 mb-5 sm:mb-6"><Users size={24} /></div>
          <h1 className="font-serif-editorial text-white text-[26px] sm:text-[36px] lg:text-[44px] leading-[1.05] sm:leading-[1.02] tracking-tight">Le livret est l'espace de l'élève</h1>
          <p className="mt-3 sm:mt-4 text-white/65 text-[13.5px] sm:text-[15px] max-w-xl mx-auto">Côté enseignant, retrouve les livrets de tes élèves rattachés depuis ton tableau de bord — avec leur progression REMC en temps réel.</p>
          <Link to="/enseignant/dashboard" data-testid="livret-go-teacher-dashboard" className="mt-6 sm:mt-8 inline-flex items-center gap-2 h-11 sm:h-12 px-5 sm:px-6 rounded-full bg-red-600 hover:bg-red-500 text-white text-[13px] sm:text-[14px] font-semibold shadow-lg shadow-red-900/40">
            Aller au tableau de bord enseignant <ArrowRight size={15} />
          </Link>
        </section>
      </PageShell>
    );
  }
  return <LivretPageEleve />;
}

function LivretPageEleve() {
  const { user, loading: authLoading, openLogin } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    (async () => {
      try {
        const data = await api.getLivret();
        setEntries(data);
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  const lookup = useMemo(() => {
    const map = {};
    for (const e of entries) {
      map[`${e.competence_code}:${e.sub_idx}`] = e;
    }
    return map;
  }, [entries]);

  const updateStatus = async (code, idx, status, self_eval) => {
    const key = `${code}:${idx}`;
    setSavingKey(key);
    try {
      const updated = await api.upsertLivret({ competence_code: code, sub_idx: idx, status, self_eval });
      setEntries((prev) => {
        const filtered = prev.filter((p) => !(p.competence_code === code && p.sub_idx === idx));
        return [...filtered, updated];
      });
    } finally {
      setSavingKey(null);
    }
  };

  const progress = useMemo(() => {
    const totalSubs = COMPETENCES.reduce((a, c) => a + c.subs.length, 0);
    const done = entries.filter((e) => e.status === 'done').length;
    return { totalSubs, done, pct: Math.round((done / totalSubs) * 100) };
  }, [entries]);

  if (authLoading) return <PageShell><div className="max-w-7xl mx-auto p-12 text-white/50">Chargement…</div></PageShell>;

  if (!user) {
    return (
      <PageShell>
        <section className="max-w-3xl mx-auto px-4 lg:px-6 mt-12 sm:mt-20 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-red-600/15 border border-red-700/30 text-red-400 mb-5 sm:mb-6"><Lock size={24} /></div>
          <h1 className="font-serif-editorial text-white text-[28px] sm:text-[40px] lg:text-[56px] leading-[1.05] sm:leading-[1.02] tracking-tight">Mon livret d’apprentissage</h1>
          <p className="mt-3 sm:mt-4 text-white/60 text-[13.5px] sm:text-[15px]">Pour suivre tes 38 compétences REMC, crée ton compte gratuit en 30 secondes.</p>
          <button onClick={openLogin} className="mt-6 sm:mt-8 h-11 sm:h-12 px-5 sm:px-6 rounded-full bg-red-600 hover:bg-red-500 text-white text-[13px] sm:text-[14px] font-semibold inline-flex items-center gap-2 shadow-lg shadow-red-900/40">Se connecter / Créer un compte</button>
        </section>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <section className="max-w-7xl mx-auto px-4 lg:px-6 mt-8 sm:mt-12">
        <SectionTitle kicker={`Bonjour ${user.name} · Pédagogique REMC`} title="Mon livret d’apprentissage" sub="Les 38 compétences à maîtriser pour le permis B. Évolue à ton rythme, auto-évalue-toi." />
        <StudentLinkTeacherWidget />
        <LivretValidationBanner />
        {/* Progress */}
        <div className="rounded-xl sm:rounded-2xl border border-white/10 bg-gradient-to-br from-red-950/20 to-[#0d0d0e] p-5 sm:p-6 lg:p-8 mb-7 sm:mb-10">
          <div className="flex items-end justify-between gap-3 sm:gap-4 flex-wrap">
            <div>
              <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-red-400 font-bold">Progression globale</div>
              <div className="mt-2 flex items-baseline gap-2 sm:gap-3 flex-wrap">
                <span className="font-serif-editorial text-white text-[40px] sm:text-[56px] leading-none">{progress.done}</span>
                <span className="text-white/40 text-[14px] sm:text-[18px]">/ {progress.totalSubs} compétences acquises</span>
              </div>
            </div>
            <div className="text-right">
              <div className="font-serif-editorial text-red-500 text-[32px] sm:text-[44px] leading-none">{progress.pct}%</div>
              <div className="text-white/40 text-[11px] sm:text-[12px] font-mono-spec uppercase tracking-wider mt-1">Avancement</div>
            </div>
          </div>
          <div className="mt-4 sm:mt-5 h-2 bg-white/5 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-red-600 to-red-400 transition-all" style={{ width: `${progress.pct}%` }} />
          </div>
        </div>

        {/* Competences */}
        {loading ? (
          <div className="text-white/50 text-center py-10 inline-flex items-center gap-2 justify-center w-full"><Loader2 size={16} className="animate-spin" /> Chargement du livret…</div>
        ) : (
          <div className="space-y-3 sm:space-y-4">
            {COMPETENCES.map((c) => (
              <div key={c.code} className="rounded-xl sm:rounded-2xl border border-white/10 bg-[#0d0d0e] overflow-hidden">
                <div className="px-4 sm:px-6 py-3 sm:py-4 bg-white/[0.02] border-b border-white/5 flex items-center gap-3 sm:gap-4">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-red-600/15 border border-red-700/30 flex items-center justify-center text-red-400 font-mono-spec font-bold text-[12px] sm:text-[14px]">{c.code}</div>
                  <div className="min-w-0">
                    <h3 className="font-serif-editorial text-white text-[17px] sm:text-[22px] leading-none">{c.title}</h3>
                    <div className="text-white/50 text-[11.5px] sm:text-[12.5px] mt-1 truncate">{c.subtitle} · {c.subs.length} sous-compétences</div>
                  </div>
                </div>
                <div className="divide-y divide-white/5">
                  {c.subs.map((sub, idx) => {
                    const k = `${c.code}:${idx}`;
                    const entry = lookup[k];
                    const status = entry?.status || 'todo';
                    const eval5 = entry?.self_eval || 0;
                    const isSaving = savingKey === k;
                    return (
                      <div key={idx} className="px-4 sm:px-6 py-3 sm:py-4 hover:bg-white/[0.02] transition">
                        <div className="flex items-center gap-2 sm:gap-4 flex-wrap">
                        <span className="font-mono-spec text-[11px] sm:text-[12px] text-white/40 shrink-0">{c.code}.{(idx + 1).toString().padStart(2, '0')}</span>
                        <span className="flex-1 text-white/85 text-[13px] sm:text-[14px] min-w-[160px] sm:min-w-[200px]">{sub}</span>
                        {entry?.teacher_status === 'acquis' && (
                          <span className="inline-flex items-center gap-1 h-6 px-2 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-[10px] font-bold uppercase tracking-wide" title={`Validé par ${entry.teacher_validated_by_name || 'ton moniteur'}`}>✓ Moniteur</span>
                        )}
                        <StatusPill status={status} />
                        {/* 5-star self-eval */}
                        <div className="flex items-center gap-0.5">
                          {[1, 2, 3, 4, 5].map((n) => (
                            <button key={n} onClick={() => updateStatus(c.code, idx, status === 'todo' ? 'inprogress' : status, n)} className="p-0.5 sm:p-1">
                              <Star size={13} className={n <= eval5 ? 'text-red-400 fill-red-400' : 'text-white/20'} />
                            </button>
                          ))}
                        </div>
                        <div className="flex gap-1 sm:gap-1.5 flex-wrap">
                          {['todo', 'inprogress', 'done'].map((s) => (
                            <button key={s} onClick={() => updateStatus(c.code, idx, s, eval5)} disabled={isSaving} className={`px-2 sm:px-2.5 h-7 rounded-md text-[10.5px] sm:text-[11px] font-semibold border transition ${status === s ? 'bg-red-600 border-red-500 text-white' : 'bg-white/5 border-white/10 text-white/60 hover:border-white/30'}`}>
                              {s === 'todo' ? 'À voir' : s === 'inprogress' ? 'En cours' : 'Acquis'}
                            </button>
                          ))}
                        </div>
                        </div>
                        {entry?.teacher_comment && (
                          <div className="mt-1.5 pl-8 sm:pl-12 text-sky-300/75 text-[12px]">💬 Ton moniteur : {entry.teacher_comment}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </PageShell>
  );
}
